// A2F-650: free-text input for Step 2 (configurator) and Step 4 (custom rules).
//
// Behavior splits by `origin`:
//
//   origin='configurator' — Step 2. Runs `detectFormatChanges`
//   client-side (color + serif keywords) and emits a FormattingPill.
//   No translate-rule call. The reason is the post-merge bug
//   diagnosed in PR #219: translate-rule's format tool returns
//   executor-shape rules (`apply["Heading 1"]["font.color"]`) that
//   don't map onto our Formatters-shape state. Reverting to the
//   client-side detection the original A2F-546 page used keeps Step 2
//   working today; a richer NL → Formatters endpoint is a separate
//   ticket.
//
//   origin='custom_rules' — Step 4. Calls translateRule and emits a
//   CustomRuleChip for any returned rule type (scan / search / format).
//   A `format` rule from this path becomes a custom taskpane button —
//   we no longer cross-route to Step 2 because the namespaces don't
//   line up (see Step 2 note above).
//
// The transient inline notice replaces the success/error hint after
// each submission and auto-dismisses in 3s.

import { useEffect, useRef, useState } from 'react'
import * as api from '../../../lib/api'
import { isUnsupported } from '../../../types'
import { Telemetry } from '../../../lib/telemetry'
import { detectFormatChanges } from '../../../lib/onboard-v2-detect'
import {
  type CustomRuleChip,
  type FormattingPill,
} from '../../../lib/onboard-v2-formatting'

const NOTICE_DISMISS_MS = 3000
const PATTERN_PREVIEW_LEN = 36

type Notice =
  | { kind: 'pill' }
  | { kind: 'rule' }
  | { kind: 'unsupported' }
  | { kind: 'error'; message: string }

interface FormattingTextBoxProps {
  placeholder: string
  buttonLabel: string
  domain: string
  origin: 'configurator' | 'custom_rules'
  onAddPill: (pill: FormattingPill) => void
  onAddRule: (chip: CustomRuleChip) => void
}

function truncateLabel(text: string, max: number): string {
  const trimmed = text.trim()
  if (trimmed.length <= max) return trimmed
  return `${trimmed.slice(0, max - 1)}…`
}

function makeId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
}

export function FormattingTextBox({
  placeholder,
  buttonLabel,
  domain,
  origin,
  onAddPill,
  onAddRule,
}: FormattingTextBoxProps) {
  const [text, setText] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [notice, setNotice] = useState<Notice | null>(null)
  const dismissRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (dismissRef.current) clearTimeout(dismissRef.current)
    }
  }, [])

  function showNotice(next: Notice) {
    setNotice(next)
    if (dismissRef.current) clearTimeout(dismissRef.current)
    dismissRef.current = setTimeout(() => setNotice(null), NOTICE_DISMISS_MS)
  }

  function applyConfigurator(trimmed: string) {
    Telemetry.onboardingFreetextSubmitted({ prompt_length: trimmed.length })
    const detected = detectFormatChanges(trimmed)
    if (!detected) {
      Telemetry.onboardingTranslateFailed({
        reason: 'unsupported',
        prompt_length: trimmed.length,
      })
      showNotice({ kind: 'unsupported' })
      return
    }
    const tableStyleFieldCount = Object.keys(detected.tableStylePatch).length
    const pill: FormattingPill = {
      id: makeId('fp'),
      label: truncateLabel(trimmed, PATTERN_PREVIEW_LEN),
      patternLength: trimmed.length,
      fieldChanges: detected.fieldChanges,
      tableStylePatch:
        tableStyleFieldCount > 0 ? detected.tableStylePatch : undefined,
      origin: 'configurator',
    }
    onAddPill(pill)
    // Telemetry counts both channels so dashboards see Step 2 activity
    // for prompts that ONLY hit the table-style path (no fieldChanges).
    Telemetry.onboardingFormattingPillAdded({
      field_count_changed:
        Object.keys(detected.fieldChanges).length + tableStyleFieldCount,
      pattern_length: trimmed.length,
      origin: 'configurator',
    })
    showNotice({ kind: 'pill' })
    setText('')
  }

  async function submitCustomRule(trimmed: string) {
    setSubmitting(true)
    Telemetry.onboardingFreetextSubmitted({ prompt_length: trimmed.length })
    try {
      const resp = await api.translateRule(trimmed, domain)
      const sourceDetail = resp.sourceDetail
      if (isUnsupported(resp.result)) {
        Telemetry.onboardingTranslateFailed({
          reason: 'unsupported',
          prompt_length: trimmed.length,
        })
        // Capture intent as a coming_soon chip so the user sees we
        // heard them even though we can't translate it today.
        onAddRule({
          id: makeId('cr'),
          label: truncateLabel(trimmed, 60),
          status: 'coming_soon',
          sourceDetail,
        })
        Telemetry.onboardingCustomRuleAdded({
          status: 'coming_soon',
          source_detail: sourceDetail,
          label: truncateLabel(trimmed, 60),
          pattern_length: trimmed.length,
        })
        showNotice({ kind: 'unsupported' })
        setText('')
        return
      }

      const rule = resp.result
      const chip: CustomRuleChip = {
        id: makeId('cr'),
        label: rule.label || truncateLabel(trimmed, 60),
        status: 'active',
        rule,
        sourceDetail,
      }
      onAddRule(chip)
      Telemetry.onboardingCustomRuleAdded({
        status: 'active',
        source_detail: sourceDetail,
        label: chip.label,
        pattern_length: trimmed.length,
      })
      showNotice({ kind: 'rule' })
      setText('')
    } catch {
      Telemetry.onboardingNetworkError({ endpoint: 'translate-rule' })
      showNotice({
        kind: 'error',
        message: "Couldn't reach the rule translator. Try again in a moment.",
      })
    } finally {
      setSubmitting(false)
    }
  }

  function submit() {
    const trimmed = text.trim()
    if (!trimmed || submitting) return
    if (origin === 'configurator') {
      applyConfigurator(trimmed)
    } else {
      void submitCustomRule(trimmed)
    }
  }

  function noticeText(n: Notice): string {
    switch (n.kind) {
      case 'pill':
        return 'Formatting applied.'
      case 'rule':
        return 'Rule added.'
      case 'unsupported':
        return origin === 'configurator'
          ? "Couldn't match that to a formatting change. Try a color or font name."
          : "Can't do that yet — we'll add it soon."
      case 'error':
        return n.message
    }
  }

  return (
    <div className="space-y-2">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
        className="flex gap-2"
      >
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-sm text-brand-charcoal placeholder:text-slate-400 outline-none focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/20"
        />
        <button
          type="submit"
          disabled={submitting || !text.trim()}
          className="rounded-lg bg-brand-orange px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-orange/90 disabled:opacity-60"
        >
          {submitting ? 'Translating…' : buttonLabel}
        </button>
      </form>
      {notice ? (
        <p
          className={`text-[11px] ${
            notice.kind === 'error'
              ? 'text-red-600'
              : notice.kind === 'unsupported'
                ? 'text-slate-500'
                : 'text-brand-orange'
          }`}
        >
          {noticeText(notice)}
        </p>
      ) : null}
    </div>
  )
}
